import { cn } from "@/lib/utils";

/**
 * Logo Sahha — mascotte Lalla Hayat (hibou) + wordmark.
 *
 * Variants :
 *   full    — mascotte + wordmark "Sahha"  (default header)
 *   mark    — mascotte uniquement (favicon, mobile)
 *   wordmark — wordmark seul (sans icône)
 *
 * Tailles : sm 28px · md 36px · lg 48px (mascotte width).
 */

const SIZES = {
  sm: { mark: 28, text: "text-lg" },
  md: { mark: 36, text: "text-xl" },
  lg: { mark: 48, text: "text-3xl" },
} as const;

export function Logo({
  className,
  variant = "full",
  size = "md",
}: {
  className?: string;
  variant?: "full" | "mark" | "wordmark";
  size?: keyof typeof SIZES;
}) {
  const dims = SIZES[size];

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      {variant !== "wordmark" && (
        <svg
          viewBox="0 0 64 64"
          width={dims.mark}
          height={dims.mark}
          className="shrink-0"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="sahhaLogoBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1570EF" />
            </linearGradient>
          </defs>

          {/* Brand background */}
          <rect x="0" y="0" width="64" height="64" rx="14" fill="url(#sahhaLogoBg)" />

          {/* Lalla Hayat — mini owl */}
          <g transform="translate(8 11)">
            <path
              d="M 24 0 C 8 0, 2 12, 2 24 C 2 36, 11 42, 24 42 C 37 42, 46 36, 46 24 C 46 12, 40 0, 24 0 Z"
              fill="#FAF5EB"
              stroke="#0F1E3A"
              strokeWidth="2"
            />
            <path
              d="M 8 7 Q 6 1, 12 2 Q 13 9, 11 12 Z"
              fill="#E5D9BD"
              stroke="#0F1E3A"
              strokeWidth="1.5"
            />
            <path
              d="M 40 7 Q 42 1, 36 2 Q 35 9, 37 12 Z"
              fill="#E5D9BD"
              stroke="#0F1E3A"
              strokeWidth="1.5"
            />

            {/* Eye outer */}
            <circle cx="16" cy="20" r="9" fill="#FDE8D4" stroke="#0F1E3A" strokeWidth="1.5" />
            <circle cx="32" cy="20" r="9" fill="#FDE8D4" stroke="#0F1E3A" strokeWidth="1.5" />

            {/* Iris */}
            <circle cx="16" cy="20" r="5" fill="#1570EF" />
            <circle cx="32" cy="20" r="5" fill="#1570EF" />

            {/* Pupils */}
            <circle cx="17" cy="19" r="2.5" fill="#0F1E3A" />
            <circle cx="33" cy="19" r="2.5" fill="#0F1E3A" />

            {/* Eye shine */}
            <circle cx="15.5" cy="17.5" r="1" fill="#FFFFFF" />
            <circle cx="31.5" cy="17.5" r="1" fill="#FFFFFF" />

            {/* Beak */}
            <path
              d="M 24 28 Q 21 33, 24 36 Q 27 33, 24 28 Z"
              fill="#F4A261"
              stroke="#0F1E3A"
              strokeWidth="1.2"
            />
          </g>
        </svg>
      )}

      {variant !== "mark" && (
        <span
          className={cn(
            dims.text,
            "font-bold tracking-tight text-[var(--sahha-primary-800)]",
          )}
          style={{ fontFamily: "var(--font-display)" }}
        >
          Sahha
        </span>
      )}
    </div>
  );
}
